import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../contexts/ApiContext';
import { Shield, Upload, Search, Filter, Eye, Download, Brain, CheckCircle, XCircle, Clock, AlertCircle, Info } from 'lucide-react';

const Controls = () => {
  const { apiGet, apiPost, bulkAnalyze } = useApi();
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCloud, setFilterCloud] = useState('');
  const [aiAnalysisResults, setAiAnalysisResults] = useState({});
  const [showAiModal, setShowAiModal] = useState(false);
  const [selectedControlForAi, setSelectedControlForAi] = useState(null);
  const [aiLoading, setAiLoading] = useState({});
  const [selectedControls, setSelectedControls] = useState(new Set());
  const [bulkAnalyzing, setBulkAnalyzing] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [existingMappings, setExistingMappings] = useState({});
  const [activeTab, setActiveTab] = useState('existing'); // 'existing' or 'ai'

  const fetchControls = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Fetching controls from /controls endpoint...');
      const data = await apiGet('/controls?limit=500');
      console.log('📊 Controls API response:', data);
      console.log('📋 Controls array:', data.controls);
      console.log('📏 Controls count:', data.controls?.length || 0);
      setControls(data.controls || []);
    } catch (error) {
      console.error('❌ Failed to fetch controls:', error);
      setError('No se pudieron cargar los controles. Verifique la conexión con la API y vuelva a intentarlo.');
      setControls([]);
    } finally {
      setLoading(false);
    }
  }, [apiGet]);

  // Function to fetch existing mappings for a control
  const fetchExistingMappings = useCallback(async (controlId) => {
    try {
      console.log(`🔍 Fetching existing mappings for control: ${controlId}`);
      const data = await apiGet(`/controls/${controlId}/mappings`);
      console.log(`📊 Found ${data.mappings?.length || 0} existing mappings for ${controlId}`);
      
      if (data.success && data.mappings) {
        setExistingMappings(prev => ({
          ...prev,
          [controlId]: data.mappings
        }));
        return data.mappings;
      }
      return [];
    } catch (error) {
      console.error(`❌ Failed to fetch mappings for ${controlId}:`, error);
      return [];
    }
  }, [apiGet]);

  // Toast notification system
  const showToast = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    const toast = { id, message, type, duration };
    setToasts(prev => [...prev, toast]);
    
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  useEffect(() => {
    fetchControls();
  }, [fetchControls]);

  // Bulk selection functions
  const handleSelectControl = (controlId) => {
    setSelectedControls(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(controlId)) {
        newSelection.delete(controlId);
      } else {
        newSelection.add(controlId);
      }
      return newSelection;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedControls(new Set(filteredControls.map(c => c.id)));
    } else {
      setSelectedControls(new Set());
    }
  };

  // Bulk analysis function
  const handleBulkAnalyze = async () => {
    const controlsToAnalyze = controls.filter(c => selectedControls.has(c.id));
    if (controlsToAnalyze.length === 0) {
      showToast('Por favor, seleccione al menos un control para analizar.', 'warning');
      return;
    }
    
    setBulkAnalyzing(true);
    
    try {
      const result = await bulkAnalyze({ 
        controls: controlsToAnalyze.map(control => ({
          control_id: control.id_control,
          id_control: control.id_control,
          title: `CIS IBM ${control.category} Control ${control.id_control}`,
          description: control.descripcion || `CIS IBM Cloud v1.1.0 security control ${control.id_control} for ${control.category} category. Ensures compliance with security standards for ${control.cloud_provider} cloud provider. Priority: ${control.severidad}.`,
          framework: 'CIS IBM Cloud v1.1.0',
          cloud_provider: control.cloud_provider || 'IBM',
          category: control.category,
          severity: control.severidad
        })),
        options: { 
          enableStabilityValidation: false,
          batchSize: 5 
        }
      });
      
      showToast(`Análisis en lote completado. ${result.results?.length || 0} controles analizados exitosamente.`, 'success');
      fetchControls();
    } catch (error) {
      showToast('Error en el análisis en lote: ' + error.message, 'error');
    } finally {
      setBulkAnalyzing(false);
      setSelectedControls(new Set());
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('org_id', 'securebank-001');

      const result = await apiPost('/controls/upload', formData);
      
      if (result.processing_status === 'completed') {
        showToast(`File uploaded and processed successfully! ${result.processed_controls} controls added.`, 'success');
      } else if (result.processing_status === 'completed_with_errors') {
        showToast(`File uploaded with some errors. ${result.processed_controls} controls added successfully. Errors: ${result.error}`, 'warning', 8000);
      } else if (result.processing_status === 'failed') {
        showToast(`File upload failed: ${result.error || 'Unknown error'}`, 'error');
      } else {
        showToast('File uploaded successfully! Processing will continue in the background.', 'info');
      }
      
      fetchControls(); // Refresh the controls list
    } catch (error) {
      showToast('Upload failed: ' + error.message, 'error');
    } finally {
      setUploadLoading(false);
      event.target.value = '';
    }
  };

  // AI Analysis Functions
  const analyzeControlWithAI = async (control) => {
    try {
      setAiLoading(prev => ({ ...prev, [control.id]: true }));
      
      const result = await apiPost('/controls/analyze', {
        control_id: control.id_control,
        id_control: control.id_control,
        title: `CIS IBM ${control.category} Control ${control.id_control}`,
        description: control.descripcion || `CIS IBM Cloud v1.1.0 security control ${control.id_control} for ${control.category} category. Ensures compliance with security standards for ${control.cloud_provider} cloud provider. Priority: ${control.severidad}.`,
        framework: 'CIS IBM Cloud v1.1.0',
        cloud_provider: control.cloud_provider || 'IBM',
        category: control.category,
        severity: control.severidad
      });

      if (result.success) {
        setAiAnalysisResults(prev => ({ 
          ...prev, 
          [control.id]: result 
        }));
        setSelectedControlForAi(control);
        setActiveTab('ai'); // Switch to AI tab for new analysis
        setShowAiModal(true);
      } else {
        showToast('AI analysis failed: ' + (result.error || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('AI analysis error:', error);
      showToast('AI analysis failed: ' + error.message, 'error');
    } finally {
      setAiLoading(prev => ({ ...prev, [control.id]: false }));
    }
  };

  const approveMappings = async (controlId, mappings) => {
    try {
      const result = await apiPost('/controls/mappings/approve-v2', {
        controlId,
        mappings: mappings.map(m => ({
          technique_id: m.technique_id,
          coverage_type: m.coverage_type,
          confidence_score: m.confidence_score,
          justification: m.justification
        }))
      });

      if (result.success) {
        showToast(`${result.approved_count} mappings approved successfully!`, 'success');
        setShowAiModal(false);
        fetchControls(); // Refresh controls to show updated mappings
      } else {
        showToast('Failed to approve mappings: ' + (result.error || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Mapping approval error:', error);
      showToast('Failed to approve mappings: ' + error.message, 'error');
    }
  };

  const downloadTemplate = () => {
    const csvContent = `ID_Control,Descripción,Cloud_Provider,Estado,Severidad
1.12,"Ensure IAM users are members of access groups and IAM policies are assigned only to access groups",IBM,ACTIVO,Medium
1.13,"Ensure a support access group has been created to manage incidents with IBM Support",IBM,ACTIVO,Medium
2.1,"Ensure encryption at rest is enabled for all storage services",Azure,PENDIENTE,High
3.1,"Enable VPC flow logs for network monitoring",GCP,ACTIVO,Medium`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', 'controles_cis_template.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const filteredControls = controls.filter(control => {
    const matchesSearch = (control.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          control.id_control?.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCloud = !filterCloud || control.cloud_provider === filterCloud;
    console.log('🔍 Filtering control:', control.id_control, 'matches search:', matchesSearch, 'matches cloud:', matchesCloud);
    return matchesSearch && matchesCloud;
  });

  const cloudProviders = [...new Set(controls.map(c => c.cloud_provider).filter(Boolean))];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Security Controls
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage and analyze security controls across cloud providers
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          {selectedControls.size > 0 && (
            <button
              onClick={handleBulkAnalyze}
              className="btn bg-purple-600 text-white hover:bg-purple-700 focus:ring-purple-500"
              disabled={bulkAnalyzing}
            >
              <Brain className="h-4 w-4 mr-2" />
              {bulkAnalyzing 
                ? `Analyzing ${selectedControls.size} Controls...` 
                : `Analyze ${selectedControls.size} Selected`}
            </button>
          )}
          <button
            onClick={downloadTemplate}
            className="btn btn-secondary"
          >
            <Download className="h-4 w-4 mr-2" />
            Download Template
          </button>
          <label className="btn btn-primary cursor-pointer">
            <Upload className="h-4 w-4 mr-2" />
            {uploadLoading ? 'Uploading...' : 'Upload CSV/Excel'}
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
              disabled={uploadLoading}
            />
          </label>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="card bg-red-50 border border-red-200">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <XCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">
                Error al cargar los controles
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={fetchControls}
                  className="text-sm font-medium text-red-800 hover:text-red-700"
                >
                  Reintentar →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Instructions */}
      <div className="card bg-blue-50 border-blue-200">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Shield className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-blue-800">
              CSV Upload Instructions
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>Columnas requeridas (CIS Benchmark): <code className="bg-blue-100 px-1 rounded">ID_Control, Descripción, Cloud_Provider, Estado, Severidad</code></p>
              <p className="mt-1">Descarga el template para ver el formato exacto. Estados: ACTIVO, PENDIENTE, DESHABILITADO. Severidad: High, Medium, Low.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Controls
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, ID, or description..."
                className="pl-10 input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cloud Provider
            </label>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                className="pl-10 input"
                value={filterCloud}
                onChange={(e) => setFilterCloud(e.target.value)}
              >
                <option value="">All Providers</option>
                {cloudProviders.map(provider => (
                  <option key={provider} value={provider}>{provider}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterCloud('');
              }}
              className="btn btn-secondary w-full"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Controls Table */}
      <div className="card p-0">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th className="w-12">
                    <input
                      type="checkbox"
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      checked={filteredControls.length > 0 && filteredControls.every(c => selectedControls.has(c.id))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </th>
                  <th>Control ID</th>
                  <th>Title & Description</th>
                  <th>Framework</th>
                  <th>Cloud Provider</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>AI Analysis</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredControls.map((control) => (
                  <tr key={control.id} className="hover:bg-gray-50">
                    <td className="w-12">
                      <input
                        type="checkbox"
                        checked={selectedControls.has(control.id)}
                        onChange={() => handleSelectControl(control.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="font-medium text-blue-600">{control.id_control}</td>
                    <td>
                      <div className="max-w-md">
                        <div className="font-medium text-gray-900">CIS IBM {control.category} - {control.id_control}</div>
                        <div className="text-sm text-gray-500 mt-1">{control.descripcion?.substring(0, 100)}...</div>
                      </div>
                    </td>
                    <td>
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">
                        CIS IBM Cloud v1.1.0
                      </span>
                    </td>
                    <td>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        control.cloud_provider === 'Azure' ? 'bg-blue-100 text-blue-800' :
                        control.cloud_provider === 'GCP' ? 'bg-green-100 text-green-800' :
                        control.cloud_provider === 'AWS' ? 'bg-orange-100 text-orange-800' :
                        control.cloud_provider === 'IBM' ? 'bg-purple-100 text-purple-800' :
                        control.cloud_provider === 'OCI' ? 'bg-red-100 text-red-800' :
                        control.cloud_provider === 'Multi-cloud' ? 'bg-gray-100 text-gray-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {control.cloud_provider}
                      </span>
                    </td>
                    <td>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        control.estado === 'ACTIVO' ? 'bg-green-100 text-green-800' :
                        control.estado === 'PENDIENTE' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {control.estado}
                      </span>
                    </td>
                    <td>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        control.severidad === 'Critical' ? 'bg-red-100 text-red-800' :
                        control.severidad === 'High' ? 'bg-orange-100 text-orange-800' :
                        control.severidad === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {control.severidad}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => analyzeControlWithAI(control)}
                        disabled={aiLoading[control.id]}
                        className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full ${
                          aiAnalysisResults[control.id] 
                            ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                            : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                        } disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                      >
                        {aiLoading[control.id] ? (
                          <>
                            <Clock className="h-3 w-3 mr-1 animate-spin" />
                            Analyzing...
                          </>
                        ) : aiAnalysisResults[control.id] ? (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Analyzed
                          </>
                        ) : (
                          <>
                            <Brain className="h-3 w-3 mr-1" />
                            Analyze with AI
                          </>
                        )}
                      </button>
                    </td>
                    <td>
                      <button 
                        onClick={async () => {
                          await fetchExistingMappings(control.id_control);
                          setSelectedControlForAi(control);
                          setActiveTab('existing'); // Set to existing mappings tab
                          setShowAiModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors"
                        title="View existing mappings"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredControls.length === 0 && !loading && (
              <div className="text-center py-12">
                <Shield className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No controls found</h3>
                <p className="text-gray-500">
                  {searchTerm || filterCloud ? 'No controls match your current filters.' : 'Upload an Excel file to get started.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card text-center">
          <div className="text-2xl font-bold text-blue-600">{controls.length}</div>
          <div className="text-sm text-gray-500">Total Controls</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-green-600">
            {Object.keys(aiAnalysisResults).length}
          </div>
          <div className="text-sm text-gray-500">AI Analyzed</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-orange-600">
            {cloudProviders.length}
          </div>
          <div className="text-sm text-gray-500">Cloud Providers</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-purple-600">
            {Math.round((Object.keys(aiAnalysisResults).length / Math.max(controls.length, 1)) * 100)}%
          </div>
          <div className="text-sm text-gray-500">AI Coverage</div>
        </div>
      </div>

      {/* Analysis Modal */}
      {showAiModal && selectedControlForAi && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <Brain className="h-8 w-8 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">AI Security Analysis Results</h2>
                    <p className="text-sm text-gray-500 mt-1">Powered by Llama 3.1 70B • Banking-optimized</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAiModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors rounded-full p-1 hover:bg-gray-100"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {/* Control Information Card */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-6 border border-blue-200">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-lg text-gray-900">
                    <span className="text-blue-600 font-mono">{selectedControlForAi.id_control}</span>
                    <span className="mx-2 text-gray-400">•</span>
                    CIS IBM {selectedControlForAi.category} Control
                  </h3>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">{selectedControlForAi.descripcion}</p>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white border border-blue-200 text-blue-700">
                    <Shield className="h-3 w-3 mr-1" />
                    CIS IBM Cloud v1.1.0
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white border border-green-200 text-green-700">
                    {selectedControlForAi.cloud_provider === 'Multi-cloud' ? '☁️' : '🏢'} {selectedControlForAi.cloud_provider}
                  </span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    selectedControlForAi.severidad === 'Critical' ? 'bg-red-100 text-red-700 border border-red-200' :
                    selectedControlForAi.severidad === 'High' ? 'bg-orange-100 text-orange-700 border border-orange-200' :
                    'bg-yellow-100 text-yellow-700 border border-yellow-200'
                  }`}>
                    ⚡ {selectedControlForAi.severidad} Priority
                  </span>
                </div>
              </div>

              {/* Mappings Section with Tabs */}
              <div className="space-y-4">
                {/* Tab Navigation */}
                <div className="border-b border-gray-200">
                  <nav className="flex space-x-8" aria-label="Tabs">
                    <button
                      onClick={() => setActiveTab('existing')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'existing'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Existing Mappings
                      {existingMappings[selectedControlForAi.id_control]?.length > 0 && (
                        <span className="ml-2 bg-blue-100 text-blue-600 py-0.5 px-2 rounded-full text-xs">
                          {existingMappings[selectedControlForAi.id_control].length}
                        </span>
                      )}
                    </button>
                    {aiAnalysisResults[selectedControlForAi.id] && (
                      <button
                        onClick={() => setActiveTab('ai')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeTab === 'ai'
                            ? 'border-green-500 text-green-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        AI Analysis
                        {aiAnalysisResults[selectedControlForAi.id]?.mappings?.length > 0 && (
                          <span className="ml-2 bg-green-100 text-green-600 py-0.5 px-2 rounded-full text-xs">
                            {aiAnalysisResults[selectedControlForAi.id].mappings.length}
                          </span>
                        )}
                      </button>
                    )}
                  </nav>
                </div>

                {/* Existing Mappings Tab */}
                {activeTab === 'existing' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">Approved MITRE Mappings</h4>
                        <p className="text-sm text-gray-500 mt-1">Previously approved mappings for this control</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                          {existingMappings[selectedControlForAi.id_control]?.length || 0} approved mappings
                        </span>
                      </div>
                    </div>

                    {existingMappings[selectedControlForAi.id_control]?.length > 0 ? (
                      <div className="space-y-3">
                        {existingMappings[selectedControlForAi.id_control].map((mapping, index) => (
                          <div key={index} className="border border-green-200 rounded-xl p-5 hover:shadow-md transition-shadow bg-green-50">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-start space-x-3 mb-3">
                                  <div className="flex-shrink-0">
                                    <span className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-mono font-semibold bg-green-700 text-white">
                                      {mapping.technique_id}
                                    </span>
                                  </div>
                                  <div className="flex-1">
                                    <h5 className="font-semibold text-gray-900">{mapping.technique_name || mapping.technique_id}</h5>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        ✅ {mapping.mapping_type || 'approved'}
                                      </span>
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {Math.round((mapping.confidence_score || 0) * 100)}% confidence
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                {mapping.justification && (
                                  <div className="mt-3 p-3 bg-white rounded-lg border border-green-200">
                                    <p className="text-sm text-gray-700">{mapping.justification}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Shield className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No approved mappings found for this control.</p>
                        <p className="text-sm mt-1">Use "Analyze with AI" to generate new mappings.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* AI Analysis Tab */}
                {activeTab === 'ai' && aiAnalysisResults[selectedControlForAi.id] && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">AI-Generated Mappings</h4>
                        <p className="text-sm text-gray-500 mt-1">AI-identified techniques that this control addresses</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          {aiAnalysisResults[selectedControlForAi.id].mappings?.length || 0} techniques identified
                        </span>
                      </div>
                    </div>

                {aiAnalysisResults[selectedControlForAi.id].mappings?.length > 0 ? (
                  <div className="space-y-3">
                    {aiAnalysisResults[selectedControlForAi.id].mappings.map((mapping, index) => (
                      <div key={index} className="border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow bg-white">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {/* Technique Header */}
                            <div className="flex items-start space-x-3 mb-3">
                              <div className="flex-shrink-0">
                                <span className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-mono font-semibold bg-gray-900 text-white">
                                  {mapping.technique_external_id || mapping.technique_id}
                                </span>
                              </div>
                              <div className="flex-1">
                                <h5 className="font-semibold text-gray-900">{mapping.technique_name}</h5>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    mapping.coverage_type === 'mitigates' ? 'bg-green-100 text-green-800' :
                                    mapping.coverage_type === 'detects' ? 'bg-blue-100 text-blue-800' :
                                    mapping.coverage_type === 'prevents' ? 'bg-purple-100 text-purple-800' :
                                    mapping.coverage_type === 'responds' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {mapping.coverage_type === 'mitigates' ? '🛡️' : 
                                     mapping.coverage_type === 'detects' ? '🔍' :
                                     mapping.coverage_type === 'prevents' ? '🚫' :
                                     mapping.coverage_type === 'responds' ? '⚡' : '•'}
                                    {mapping.coverage_type.charAt(0).toUpperCase() + mapping.coverage_type.slice(1)}
                                  </span>
                                  {mapping.banking_relevance && (
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      mapping.banking_relevance === 'high' ? 'bg-red-100 text-red-800' :
                                      mapping.banking_relevance === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-green-100 text-green-800'
                                    }`}>
                                      🏦 Banking: {mapping.banking_relevance}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Justification */}
                            <div className="bg-gray-50 rounded-lg p-3 mb-3">
                              <div className="text-sm text-gray-700 leading-relaxed">
                                <span className="font-medium text-gray-900">AI Justification:</span>
                                <div className="mt-2 space-y-2">
                                  {mapping.justification.split(/(?=Directness:|Mechanism:|Adversary Impact:)/).filter(Boolean).map((part, idx) => (
                                    <div key={idx} className="flex">
                                      <span className="font-semibold text-blue-700 mr-2 min-w-[100px]">
                                        {part.match(/^(Directness|Mechanism|Adversary Impact):/)?.[1]}:
                                      </span>
                                      <span className="flex-1">
                                        {part.replace(/^(Directness|Mechanism|Adversary Impact):\s*/, '')}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                            
                            {/* Metadata */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4 text-xs text-gray-500">
                                <span className="inline-flex items-center">
                                  <div className="w-full bg-gray-200 rounded-full h-2 mr-2" style={{width: '60px'}}>
                                    <div 
                                      className="bg-blue-600 h-2 rounded-full" 
                                      style={{width: `${mapping.confidence_score * 60}px`}}
                                    />
                                  </div>
                                  <span className="font-medium">{(mapping.confidence_score * 100).toFixed(0)}% confidence</span>
                                </span>
                              </div>
                              {mapping.tactics && mapping.tactics.length > 0 && (
                                <span className="text-xs text-gray-500">
                                  Tactics: <span className="font-medium">{mapping.tactics.join(', ')}</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Brain className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No MITRE ATT&CK mappings found for this control.</p>
                  </div>
                )}

                {/* Analysis Metadata */}
                {aiAnalysisResults[selectedControlForAi.id].metadata && (
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-5 mt-6 border border-gray-200">
                    <h5 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <Brain className="h-4 w-4 mr-2 text-gray-600" />
                      Analysis Performance Metrics
                    </h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="text-xs text-gray-500 uppercase tracking-wider">Processing Time</div>
                        <div className="text-lg font-bold text-gray-900 mt-1">
                          {(aiAnalysisResults[selectedControlForAi.id].metadata.processingTime / 1000).toFixed(1)}s
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="text-xs text-gray-500 uppercase tracking-wider">Techniques Analyzed</div>
                        <div className="text-lg font-bold text-gray-900 mt-1">
                          {aiAnalysisResults[selectedControlForAi.id].metadata.candidatesRetrieved || '695'}
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="text-xs text-gray-500 uppercase tracking-wider">Avg Confidence</div>
                        <div className="text-lg font-bold text-gray-900 mt-1">
                          {(aiAnalysisResults[selectedControlForAi.id].metadata.avgConfidence * 100).toFixed(0)}%
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="text-xs text-gray-500 uppercase tracking-wider">AI Model</div>
                        <div className="text-sm font-bold text-gray-900 mt-1">
                          Llama 3.1 70B
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                  </div>
                )}

              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Analysis completed at {new Date().toLocaleTimeString()}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowAiModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    Close
                  </button>
                  {aiAnalysisResults[selectedControlForAi.id].mappings?.length > 0 && (
                    <button
                      onClick={() => approveMappings(
                        selectedControlForAi.id_control, 
                        aiAnalysisResults[selectedControlForAi.id].mappings
                      )}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors inline-flex items-center"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve All {aiAnalysisResults[selectedControlForAi.id].mappings.length} Mappings
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5 transform transition-all duration-300 ease-in-out animate-slide-in ${
              toast.type === 'success' ? 'border-l-4 border-green-400' :
              toast.type === 'error' ? 'border-l-4 border-red-400' :
              toast.type === 'warning' ? 'border-l-4 border-yellow-400' :
              'border-l-4 border-blue-400'
            }`}
          >
            <div className="flex-1 w-0 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {toast.type === 'success' && <CheckCircle className="h-5 w-5 text-green-400" />}
                  {toast.type === 'error' && <XCircle className="h-5 w-5 text-red-400" />}
                  {toast.type === 'warning' && <AlertCircle className="h-5 w-5 text-yellow-400" />}
                  {toast.type === 'info' && <Info className="h-5 w-5 text-blue-400" />}
                </div>
                <div className="ml-3 w-0 flex-1 pt-0.5">
                  <p className="text-sm font-medium text-gray-900">
                    {toast.type === 'success' && 'Éxito'}
                    {toast.type === 'error' && 'Error'}
                    {toast.type === 'warning' && 'Advertencia'}
                    {toast.type === 'info' && 'Información'}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">{toast.message}</p>
                </div>
              </div>
            </div>
            <div className="flex border-l border-gray-200">
              <button
                onClick={() => removeToast(toast.id)}
                className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <XCircle className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Controls;